<template>
  <ToastDialog :title="toastTitle" :message="toastMessage" :error="toastError" />
  <div class="question-detail-expert d-flex h-100 no-grow">
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Caricamento...</span>
      </div>
      <div class="loading-text">Caricamento in corso...</div>
    </div>
    <div v-if="isChangingState" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Salvataggio in corso...</span>
      </div>
      <div class="loading-text">Salvataggio in corso...</div>
    </div>

    <div class="left-toggle d-flex align-items-center" @click="toggleLeftPanel">
      <i :class="leftPanelVisible ? 'bi bi-chevron-left' : 'bi bi-chevron-right'"></i>
    </div>

    <SimpleModal v-model="showConfirmModal"
      title="Procedendo, la risposta al quesito sarà inviata alla Stazione Appaltante" :buttons="['Modifica', 'Invia']"
      @chose="handleConfirmChoice" />

    <SimpleModal v-model="showSuccessModal" title="Quesito evaso" :buttons="['Ok']" @chose="handleSuccessModalChoice" />

    <!-- Pannello sinistro -->
    <transition name="slide">
      <div v-if="leftPanelVisible" class="left-panel p-3 border-end" style="flex: 1">

        <!-- Switch -->
        <div v-if="isAssigned" class="toggle-3d mb-3">
          <input type="checkbox" id="leftViewToggle" class="toggle-switch" v-model="leftView" true-value="topics"
            false-value="articles">
          <label for="leftViewToggle" class="toggle-label">
            <span class="toggle-option toggle-left">D.lgs. 36/2023</span>
            <span class="toggle-option toggle-right">
              Commenti
              <span v-if="comments.length > 0" class="comment-count">{{ comments.length }}</span>
            </span>
          </label>
        </div>

        <div v-else class="mb-3">
        </div>

        <!-- CONTENUTO VARIABILE -->
        <div class="left-content">

          <!-- VISTA ARTICOLI -->
          <div v-if="leftView === 'articles'" class="left-content">
            <ArticlesView class="compact-sidebar" />

            <ModalView v-if="showArticleModal" v-model="showArticleModal" title="Dettaglio Articolo"
              icon="file-earmark-text" @close="closeArticleModal">
              <ArticleView :articleId="modalArticleId" :version="modalVersion"
                :key="selectedArticleId + '-' + modalCounter" />
            </ModalView>
          </div>

          <!-- VISTA COMMENTI -->
          <div v-else-if="leftView === 'topics'" class="comments-view">
            <div class="comments-header d-flex align-items-center justify-content-between mb-3">
              <h5 class="m-0">Commenti</h5>
              <button class="btn btn-sm btn-light text-secondary border-0 d-flex align-items-center"
                @click="exportCommentsPdf">
                Esporta
                <i class="bi bi-arrow-down-short ms-1"></i>
              </button>
            </div>
            <div v-for="comment in comments" :key="comment.id" class="comment-postit">
              <div class="comment-header d-flex align-items-center mb-2">
                <div class="avatar me-2">{{ getInitials(comment.user) }}</div>
                <div class="user-info me-auto">
                  <div class="user-name">{{ comment.user.name }} {{ comment.user.surname }}</div>
                  <div class="comment-date">{{ formatDate(comment.created_at) }}</div>
                </div>
              </div>

              <div class="comment-text" :class="{ collapsed: !comment.expanded }" v-html="sanitize(comment.text)"></div>

              <div v-if="needsReadMore(comment)" class="text-end mt-1">
                <button class="btn btn-sm btn-link p-0" @click="toggleComment(comment)">
                  {{ comment.expanded ? 'Chiudi' : 'Leggi di più...' }}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </transition>

    <!-- Pannello destro -->
    <div class="right-panel p-3" :style="{ flex: leftPanelVisible ? 3 : 1, display: 'flex', flexDirection: 'column' }">

      <!-- Header quesito -->
      <div class="question-header mb-3 border-bottom pb-3">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h4>{{ question.object }}</h4>

          <div class="d-flex gap-2">
            <!-- Freccia indietro -->
            <button class="btn btn-outline-secondary btn-sm" @click="goBackWithFilters"
              title="Torna all'elenco dei quesiti">
              <i class="bi bi-arrow-left"></i>
            </button>

            <template v-if="isAssigned">
              <button :disabled="!canTakeOver" @click="prendiInCarico"
                class="btn btn-secondary btn-sm" title="Prendi in carico il quesito">
                <i class="bi bi-hand-index-thumb"></i>
              </button>
              <button class="btn btn-warning btn-sm"
                :disabled="question.state_id === 'ASSEGNATO' || question.state_id === 'EVASO' || isAnswered"
                @click="saveDraft" data-bs-toggle="tooltip" title="Salva">
                <i class="bi bi-floppy"></i>
              </button>
              <button class="btn btn-sm btn-primary"
                :disabled="question.state_id === 'ASSEGNATO' || question.state_id === 'EVASO' || isAnswered || !answerText"
                @click="showConfirmModal = true" data-bs-toggle="tooltip" title="Rispondi">
                <i class="bi bi-arrow-right"></i>
              </button>
            </template>

            <template v-else>
              <button v-if="canEditArticlesAndTopics" class="btn btn-warning btn-sm"
                :disabled="question.state_id === 'ASSEGNATO' || question.state_id === 'EVASO' || isAnswered"
                @click="saveArticlesAndTopics" data-bs-toggle="tooltip" title="Salva modifiche articoli/argomenti">
                <i class="bi bi-floppy"></i>
              </button>
              <button v-if="canComment" class="btn btn-primary btn-sm" @click="openCommentEditor">
                Commenta
              </button>
            </template>
          </div>
        </div>

        <div class="text-muted small mb-1 d-flex align-items-center justify-content-between">
          <div class="d-flex gap-2 align-items-center">
            <strong><span>#{{ question.id }}</span></strong>
            <strong><span>{{ question.organization }}</span></strong>
            <!--<strong><span>Friuli Venezia Giulia</span></strong>-->
            <strong><span><i class="bi bi-clock"></i> Data creazione: {{ formatDate(question.created_at)
                }}</span></strong>
            <strong><span><i class="bi bi-clock"></i> Ultima modifica: {{ formatDate(question.updated_at)
                }}</span></strong>
          </div>
        </div>

        <!-- Articoli correlati -->
        <div class="text-muted small mb-1 d-flex align-items-center gap-2 flex-wrap">
          <strong>Articoli correlati:</strong>

          <div class="tag-list">
            <div v-for="(row, rowIndex) in groupedArticles" :key="rowIndex" class="tag-row-wrapper">
              <div v-for="art in row" :key="art.id" class="tag-row">
                <span class="tag-box"><strong>{{ getArticleDisplayTitle(art) }}</strong></span>
                <button
                  v-if="(question.expert_id === $root.user?.id && ['RISPOSTA_IN_BOZZA', 'IN_LAVORAZIONE'].includes(question.state_id)) || canEditArticlesAndTopics"
                  class="remove-btn" @click="removeArticle(articlesSnapshot.indexOf(art))">×</button>
              </div>
            </div>
          </div>

          <div class="dropdown"
            v-if="((question.expert_id === $root.user?.id && ['RISPOSTA_IN_BOZZA', 'IN_LAVORAZIONE'].includes(question.state_id)) || canEditArticlesAndTopics) && question.legislation_id !== null">
            <button class="btn btn-primary small-rect-btn" data-bs-toggle="dropdown">+</button>

            <ul class="dropdown-menu p-2" style="max-height:250px; overflow-y:auto; min-width:250px;">
              <li class="mb-2 px-2">
                <input type="text" class="form-control form-control-sm" placeholder="Cerca..." v-model="articleSearch">
              </li>

              <li v-for="article in filteredArticles" :key="article.id" class="form-check">
                <input class="form-check-input" type="checkbox" :id="'article-' + article.id" :value="article.id"
                  v-model="selectedArticlesIds">
                <label class="form-check-label ms-2" :for="'article-' + article.id">
                  {{ article.versions[article.versions.length - 1].title }}
                </label>
              </li>
            </ul>
          </div>
        </div>

        <!-- Argomenti correlati -->
        <div class="d-flex align-items-center gap-2 flex-wrap justify-content-between">
          <div class="argomenti-container">
            <div class="text-muted small mb-1 d-flex align-items-center gap-2">

              <strong>Argomenti correlati:</strong>

              <div class="tag-list">
                <div v-for="(row, rowIndex) in groupedTopics" :key="rowIndex" class="tag-row-wrapper">
                  <div v-for="topic in row" :key="topic.id" class="tag-row">
                    <span class="tag-box"><strong>{{ topic.title }}</strong></span>
                    <button
                      v-if="(['RISPOSTA_IN_BOZZA', 'IN_LAVORAZIONE'].includes(question.state_id) && this.question.expert_id === this.$root.user?.id) || canEditArticlesAndTopics"
                      class="remove-btn" @click="removeTopic(topicsSnapshot.indexOf(topic))">×</button>
                  </div>
                </div>
              </div>

              <div class="dropdown"
                v-if="(['RISPOSTA_IN_BOZZA', 'IN_LAVORAZIONE'].includes(question.state_id) && this.question.expert_id === this.$root.user?.id) || canEditArticlesAndTopics">

                <button class="btn btn-primary small-rect-btn" data-bs-toggle="dropdown">+</button>

                <ul class="dropdown-menu p-2" style="max-height:250px; overflow-y:auto; min-width:250px;">
                  <li class="mb-2 px-2">
                    <input type="text" class="form-control form-control-sm" placeholder="Cerca..."
                      v-model="topicSearch">
                  </li>

                  <li v-for="topic in filteredTopics" :key="topic.id" class="form-check">
                    <input class="form-check-input" type="checkbox" :id="'topic-' + topic.id" :value="topic.id"
                      v-model="selectedTopicsIds">
                    <label class="form-check-label ms-2" :for="'topic-' + topic.id">{{ topic.title }}</label>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <span class="badge status-label" :class="`status-${formatState(question.state_id).color}`">
            {{ formatState(question.state_id).label }}
          </span>
        </div>
      </div>

      <!-- Corpo quesito -->
      <div v-if="isAssigned" class="question-body d-flex flex-grow-1 gap-3">

        <!-- Quesito -->
        <div class="quesito-panel d-flex flex-column gap-3 no-grow" style="flex: 1; min-height: 200px;">

          <div class="toggle-3d mb-3">
            <input type="checkbox" id="quesitoToggle" class="toggle-switch" v-model="quesitoView" true-value="editable"
              false-value="original">
            <label for="quesitoToggle" class="toggle-label">
              <span class="toggle-option toggle-left">Quesito originale</span>
              <span class="toggle-option toggle-right">Revisione Quesito</span>
            </label>
          </div>

          <div v-if="quesitoView === 'original'" class="p-2 border rounded original-box">
            {{ question.originalText }}
          </div>

          <div v-else class="d-flex flex-column border p-2 rounded no-grow"
            style="flex:1; min-height:200px; overflow-y:auto;">
            <TiptapEditor :key="question.id" v-model="editableQuestion" class="tiptap-wrapper" :editable="canEdit"
              :state-id="question.state_id" />
          </div>

        </div>

        <!-- Risposta -->
        <div v-if="isAssigned" class="d-flex flex-column border p-2 rounded no-grow risposta-panel"
          style="flex: 2 1 0%; overflow-y: auto; min-width:0;">
          <div class="fw-bold mb-2">Risposta</div>
          <TiptapEditor :key="question.id" v-model="answerText" class="tiptap-wrapper" :editable="canEdit"
            :state-id="question.state_id" />
        </div>
      </div>

      <!-- Corpo quesito NON ASSEGNATARIO -->
      <div v-else class="quesito-thread-panel d-flex flex-column gap-3 no-grow" style="flex: 1; min-height: 200px;">

        <!-- Editor per commentare -->
        <div v-if="showCommentEditor" class="border rounded p-2 mb-2 risposta-panel">
          <TiptapEditor v-model="commentDraft" :editable="true" class="tiptap-wrapper" />
          <div class="mt-2 text-end">
            <button class="btn btn-secondary btn-sm me-2" @click="cancelCommentEdit">Annulla</button>
            <button class="btn btn-primary btn-sm" @click="submitComment">Commenta</button>
          </div>
        </div>

        <div class="thread-box d-flex flex-column gap-3" style="overflow-y:auto; flex:1; min-height:200px;">
          <div v-for="item in filteredThread" :key="item._uid" :class="[
            'thread-item',
            'mb-3',
            {
              'comment-yellow': item.type === 'comment',
              'thread-item-original': item.type === 'original',
              'thread-item-revision': item.type === 'revision',
              'thread-item-answer': item.type === 'answer',
              'border rounded p-2': !['comment', 'original', 'revision', 'answer'].includes(item.type)
            }
          ]">

            <!-- Label -->
            <span v-if="['original', 'revision', 'answer'].includes(item.type)" class="thread-label" :class="{
              'label-original': item.type === 'original',
              'label-revision': item.type === 'revision',
              'label-answer': item.type === 'answer'
            }">
              {{ item.type === 'original' ? 'Quesito originale' : item.type === 'revision' ? 'Revisione' : 'Risposta' }}
            </span>

            <!-- Header -->
            <div class="thread-header d-flex align-items-center mb-2 thread-header-common">
              <div class="avatar me-2">
                <span v-if="item.type === 'original'">{{ item.user?.initials }}</span>
                <span v-else>{{ getInitials(item.user) }}</span>
              </div>
              <div class="user-info me-auto">
                <div class="user-name">{{ item.user?.name }} {{ item.user?.surname }}</div>
                <div class="comment-date">{{ formatDate(item.date) }}</div>
              </div>
            </div>

            <!-- Contenuto -->
            <div class="thread-content" v-html="sanitize(item.html || item.text)"></div>

            <!-- Bottoni modifica/elimina -->
            <div v-if="item.type === 'comment' && item.user?.id === $root.user?.id"
              class="mt-2 d-flex justify-content-end gap-2">
              <button class="btn btn-sm btn-primary" style="padding: 0.25rem 0.5rem; font-size: 0.75rem;"
                @click="startEditComment(item)">
                Modifica
              </button>
              <button class="btn btn-sm btn-danger" style="padding: 0.25rem 0.5rem; font-size: 0.75rem;"
                @click="confirmDeleteComment(item)">
                Elimina
              </button>
            </div>

            <!-- SimpleModal per conferma eliminazione -->
            <SimpleModal v-model="showDeleteModal" title="Sei sicuro di eliminare il tuo commento?"
              :buttons="['Si, elimino', 'No, annulla']" @chose="handleDeleteChoice" />

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { sanitize } from '../../../utils/sanitize';
import ToastDialog from '../../../components/common/ToastDialog.vue';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import toastable from '../../../mixins/toastable';
import TiptapEditor from '../../../components/common/TiptapEditor.vue';
import ArticlesView from '../ArticlesView.vue';
import ArticleView from '../ArticleView.vue'
import ModalView from '../../../components/common/ModalView.vue';
import SimpleModal from '../../../components/common/SimpleModal.vue';
import { STATE_CONFIG } from '../../../utils/stateConfig';

export default {
  name: 'QuestionDetailUOCView',
  components: { ToastDialog, QuillEditor, TiptapEditor, ArticlesView, ArticleView, ModalView, SimpleModal },
  mixins: [toastable],
  props: ['elementId', 'articleId', 'version'],
  data() {
    return {
      isLoading: true,
      isChangingState: false,
      question: {
        object: '',
        text: '',
        topics: [],
        articles: [],
        legislation_id: '',
        answer: '',
      },
      legislations: [],
      availableTopics: [],
      availableArticles: [],
      topicSearch: '',
      articleSearch: '',
      answerText: '',
      topicsSnapshot: [],
      articlesSnapshot: [],
      revisionText: null,
      elements: [],
      leftView: 'articles',
      leftPanelVisible: true,
      showArticleModal: false,
      modalArticleId: null,
      modalVersion: null,
      comments: [],
      selectedTopicsIds: [],
      selectedArticlesIds: [],
      modalCounter: 0,
      localElementId: this.elementId,
      editableQuestion: '',
      quesitoView: 'original',
      showConfirmModal: false,
      isAnswered: false,
      showSuccessModal: false,
      isAssegned: false,
      showCommentEditor: false,
      showDeleteModal: false,
      commentToDelete: null,
    };
  },
  computed: {
    canReassign() {
      const allowedStates = ['IN_LAVORAZIONE', 'ASSEGNATO', 'INVIATO'];
      return this.$root.user?.permissions?.includes('assign-question') &&
        allowedStates.includes(this.question.state_id);
    },
    filteredThread() {
      const allowedStates = [
        'EVASO',
        'IN_SALA_ATTESA',
        'IN_ESAMINA_UOC',
        'NON_ASSEVERATO',
        'ASSEVERATO'
      ];

      const typePriority = {
        answer: 3,
        revision: 2,
        comment: 1,
        original: 0
      };

      return this.combinedThread
        .filter(item => {
          if (!item) return false;
          if (item.type === 'original' || item.type === 'comment') return true;
          return allowedStates.includes(this.question.state_id);
        })
        .sort((a, b) => {
          const dateA = new Date(a.date || a.created_at).getTime();
          const dateB = new Date(b.date || b.created_at).getTime();

          if (dateA !== dateB) {
            return dateB - dateA;
          }

          const prioA = typePriority[a.type] ?? 0;
          const prioB = typePriority[b.type] ?? 0;

          return prioB - prioA;
        });
    },
    canComment() {
      //return (this.question?.state_id === 'IN_LAVORAZIONE' || this.question?.state_id === 'EVASO' || this.question?.state_id === 'RISPOSTA_IN_BOZZA');
      const normalComment = ['IN_LAVORAZIONE', 'EVASO', 'RISPOSTA_IN_BOZZA'].includes(this.question?.state_id);
      const gestoreComment = this.isGestore && this.question?.state_id === 'ASSEGNATO';
      return normalComment || gestoreComment;
    },
    combinedThread() {
      const items = [];

      // Quesito originale
      items.push({
        _uid: 'q-original',
        type: 'original',
        label: 'Quesito originale',
        text: this.question.originalText,
        date: this.question.created_at,
        user: {
          name: this.question.created_by?.organization,
          surname: '',
          initials: this.getInitialsFromOrg(this.question.created_by?.organization)
        }
      });

      // Revisione quesito
      if (this.question.text !== this.question.originalText) {
        items.push({
          _uid: 'q-revision',
          type: 'revision',
          label: 'Revisione quesito',
          text: this.question.text,
          date: this.question.updated_at,
          user: {
            name: this.question.modified_by?.name,
            surname: this.question.modified_by?.surname,
          }
        });
      }

      // Risposta
      if (this.question.answer) {
        items.push({
          _uid: 'answer',
          type: 'answer',
          label: 'Risposta',
          text: this.question.answer,
          date: this.question.sending_date,
          user: {
            name: this.question.sending_user?.name,
            surname: this.question.sending_user?.surname,
          }
        });
      }

      // Commenti
      this.comments.forEach(c => {
        items.push({
          ...c,
          type: 'comment',
          label: 'Commento',
          subLabel: c.user ? `${c.user.name} ${c.user.surname}` : '',
          date: c.created_at,
          _uid: `comment-${c.id}`
        });
      });

      return items.sort((a, b) =>
        new Date(b.date || b.created_at) - new Date(a.date || a.created_at)
      );
    },
    isAssigned() {
      //return this.question.expert_id === this.$root.user?.id;
    },
    canEdit() {
      //return this.question.expert_id === this.$root.user?.id &&
       // (this.question.state_id === 'IN_LAVORAZIONE' || this.question.state_id === 'RISPOSTA_IN_BOZZA');
    },
    async prendiInCarico() {
      this.isChangingState = true;
      const today = new Date();
      const expiresOn = new Date(today);
      expiresOn.setDate(today.getDate() + 15);

      const payload = {
        id: this.question.id,
        state_id: 'IN_LAVORAZIONE',
        //expires_on: expiresOn.toISOString().split('T')[0]
      };

      try {
        const response = await this.$root.$api.put(`/questions/${this.question.id}`, payload);

        this.question.state_id = response.data.question.state_id;
        //this.question.expires_on = response.data.question.expires_on;
        this.question.updated_at = response.data.question.updated_at;

        this.showSuccessToast("Successo", "Quesito preso in carico");
      } catch (err) {
        console.error(err);
        this.showErrorToast("Errore", "Impossibile prendere in carico il quesito");
      } finally {
        this.isChangingState = false;
      }
    },
    canTakeOver() {
      return this.question.expert_id != null &&
        this.question.expert_id === this.$root.user?.id &&
        this.question.state_id === 'ASSEGNATO';
    },
    filteredArticles() {
      let articles = this.availableArticles.slice(); // copia per non modificare l'originale

      if (this.articleSearch) {
        articles = articles.filter(a =>
          this.formatArticleTitle(a.versions[a.versions.length - 1].title)
            .toLowerCase()
            .includes(this.articleSearch.toLowerCase())
        );
      }

      // ordino per id o per titolo
      return articles.sort((a, b) => a.id - b.id);
    }
    ,
    filteredTopics() {
      if (!this.topicSearch) return this.availableTopics;
      return this.availableTopics.filter(t => t.title.toLowerCase().includes(this.topicSearch.toLowerCase()));
    },
    groupedTopics() {
      const chunkSize = 4;
      const result = [];
      for (let i = 0; i < this.topicsSnapshot.length; i += chunkSize) {
        result.push(this.topicsSnapshot.slice(i, i + chunkSize));
      }
      return result;
    },
    groupedArticles() {
      const chunkSize = 4;
      const result = [];
      for (let i = 0; i < this.articlesSnapshot.length; i += chunkSize) {
        result.push(this.articlesSnapshot.slice(i, i + chunkSize));
      }
      return result;
    },
    isGestore() {
      return this.$root.user?.permissions?.includes('assign-question');
    },
    canEditArticlesAndTopics() {
      return this.isGestore && this.question?.state_id === 'INVIATO';
    }

  },
  watch: {
    elementId: {
      immediate: true,
      handler(newId, oldId) {
        //if (!oldId || !newId) return;
        if (newId === "0") return;
        if (newId && oldId && newId !== oldId) {
          this.openModalArticle();
        }
      }
    },
    version(newVersion, oldVersion) {
      if (newVersion) {
        //this.reloadQuestion();
      }
    },
    question: {
      deep: true,
      immediate: true,
      handler(newQ) {
        this.editableQuestion = newQ.text;
        this.answerText = newQ.answer;
      }
    },
    selectedArticlesIds(newIds) {
      if (this.availableArticles.length === 0) return;
      this.articlesSnapshot = this.availableArticles.filter(a => newIds.includes(a.id));
    },
    selectedTopicsIds(newIds) {
      this.topicsSnapshot = this.availableTopics.filter(t => newIds.includes(t.id));
    },
    availableArticles(newArticles) {
      if (!this.selectedArticlesIds?.length) return;
      this.articlesSnapshot = newArticles.filter(a => this.selectedArticlesIds.includes(a.id));
    }
  },
  methods: {
    sanitize(html) {
      return sanitize(html);
    },
    async exportCommentsPdf() {
      try {
        const response = await this.$root.$api.get(`/user/pdf-download-comments/${this.question.id}`, {
          responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Commenti_Quesito_${this.question.id}.pdf`);
        document.body.appendChild(link);
        link.click();


        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

      } catch (error) {
        console.error('Errore durante l\'esportazione del PDF:', error);
        this.$toast?.error?.('Errore durante l\'esportazione del PDF');
      }
    },
    applyFilterFromParams(filters) {
      this.selectedTopicsIds = filters.topics || [];
      this.selectedArticlesIds = filters.articles || [];
      this.topicSearch = filters.searchTopic || '';
      this.articleSearch = filters.searchArticle || '';
    },
    goBackWithFilters() {

      const filters = {
        topics: this.selectedTopicsIds,
        articles: this.selectedArticlesIds,
        searchTopic: this.topicSearch,
        searchArticle: this.articleSearch,
      };

      this.$router.push({
        name: 'questions.list',
        query: { filters: JSON.stringify(filters) }
      });
    },
    formatState(state) {
      return STATE_CONFIG[state] || {
        label: state,
        class: 'state-bozza',
        color: 'gray'
      };
    },
    confirmDeleteComment(item) {
      this.commentToDelete = item;
      this.showDeleteModal = true;
    },

    async handleDeleteChoice(choice) {
      if (choice === 'Si, elimino' && this.commentToDelete) {
        try {
          await this.$root.$api.delete(`/questions/${this.question.id}/comments/${this.commentToDelete.id}`);
          this.comments = this.comments.filter(c => c.id !== this.commentToDelete.id);
          this.showSuccessToast('Successo', 'Commento eliminato');
        } catch (e) {
          console.error(e);
          this.showErrorToast('Errore', 'Impossibile eliminare il commento');
        }
      }
      // reset
      this.commentToDelete = null;
      this.showDeleteModal = false;
    },
    cancelCommentEdit() {
      this.showCommentEditor = false;
      this.commentDraft = '';
      this.editingCommentId = null;
    },
    getInitialsFromOrg(org) {
      if (!org) return '';

      const stopWords = [
        'di', 'del', 'della', 'delle', 'dei', 'degli',
        'e', 'ed', 'da', 'in', 'su', 'per', 'con'
      ];

      return org
        .split(' ')
        .filter(word => word.length > 1)
        .filter(word => !stopWords.includes(word.toLowerCase()))
        .map(word => word[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    },
    openCommentEditor() {
      this.showCommentEditor = true;
      this.commentDraft = '';
      this.editingCommentId = null;
      this.$nextTick(() => {
      });
    },
    async submitComment() {
      if (!this.commentDraft || !this.commentDraft.trim()) return;


      try {
        if (this.editingCommentId) {
          const payload = { text: this.commentDraft };
          const resp = await this.$root.$api.put(`/questions/${this.question.id}/comments/${this.editingCommentId}`, payload);
          const idx = this.comments.findIndex(c => c.id === this.editingCommentId);
          if (idx !== -1) this.comments.splice(idx, 1, { ...resp.data, expanded: false });
          this.showSuccessToast('Successo', 'Commento modificato');
        } else {
          const payload = { text: this.commentDraft };
          const resp = await this.$root.$api.post(`/questions/${this.question.id}/comments`, payload);
          this.comments.push({ ...resp.data, expanded: false });
          this.showSuccessToast('Successo', 'Commento aggiunto');
        }
        this.cancelCommentEdit();
      } catch (e) {
        console.error(e);
        this.showErrorToast('Errore', 'Impossibile salvare il commento');
      }
    },
    startEditComment(item) {
      this.editingCommentId = item.id;
      this.commentDraft = item.text || item.html || '';
      this.showCommentEditor = true;
    },
    async deleteComment(item) {
      if (!confirm('Confermi eliminazione commento?')) return;
      try {
        await this.$root.$api.delete(`/questions/${this.question.id}/comments/${item.id}`);
        this.comments = this.comments.filter(c => c.id !== item.id);
        this.showSuccessToast('Successo', 'Commento eliminato');
      } catch (e) {
        console.error(e);
        this.showErrorToast('Errore', 'Impossibile eliminare il commento');
      }
    },
    handleConfirmChoice(choice) {
      this.showConfirmModal = false;
      if (choice === 'Invia') {
        this.replyToQuestionConfirmed();
      }
    },
    async saveDraft() {
      this.isChangingState = true;
      const payload = {
        id: this.question.id,
        text: this.editableQuestion,
        answer: this.answerText,
        topics: this.topicsSnapshot.map(t => t.id),
        articles: this.articlesSnapshot.map(a => a.id),
        state_id: 'IN_LAVORAZIONE',
      };

      try {
        const response = await this.$root.$api.put(`/questions/${this.question.id}`, payload);
        this.question.updated_at = response.data.question.updated_at;
        this.showSuccessToast("Successo", "Salvato in bozza");
        this.question.state_id = "IN_LAVORAZIONE";
      } catch (err) {
        this.showErrorToast("Errore", "Impossibile salvare in bozza");
      } finally {
        this.isChangingState = false;
      }
    },
    /**async autosaveQuestion() {
      const payload = {
        id: this.question.id,
        text: this.editableQuestion,
        answer: this.answerText,
        topics: this.topicsSnapshot.map(t => t.id),
        articles: this.articlesSnapshot.map(a => a.id),
        autosave: true
      };
  
      try {
        await this.$root.$api.put(`/questions/${this.question.id}/autosave`, payload);
        console.log('Autosave completato');
      } catch (e) {
        console.warn('Errore autosave', e);
      }
    },*/
    async saveArticlesAndTopics() {
      if (!this.question?.id) return;

      this.isChangingState = true;

      const payload = {
        id: this.question.id,
        topics: this.topicsSnapshot.map(t => t.id),
        articles: this.articlesSnapshot.map(a => a.id),
      };

      try {
        const response = await this.$root.$api.put(`/questions/${this.question.id}`, payload);
        this.question.updated_at = response.data.question.updated_at;
        this.showSuccessToast("Successo", "Articoli e argomenti salvati correttamente");
      } catch (err) {
        console.error(err);
        this.showErrorToast("Errore", "Impossibile salvare articoli/argomenti");
      } finally {
        this.isChangingState = false;
      }
    },
    async autosaveBeforeUnload() {
      if (!['RISPOSTA_IN_BOZZA', 'IN_LAVORAZIONE'].includes(this.question.state_id) || !this.isAssigned) {
        console.log('Autosave saltato: stato non consentito', this.question.state_id);
        return;
      }
      const payload = {
        id: this.question.id,
        text: this.editableQuestion,
        answer: this.answerText,
        topics: this.topicsSnapshot.map(t => t.id),
        articles: this.articlesSnapshot.map(a => a.id),
        //state_id: 'RISPOSTA_IN_BOZZA',
        state_id: 'IN_LAVORAZIONE',
        autosave: true
      };

      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      /**const ok = navigator.sendBeacon(
        `${this.$root.config.VUE_APP_API_URL}/questions/${this.question.id}/autosave`,
        blob
      );*/

      await this.$root.$api.post(`/questions/${this.question.id}/autosave`, payload);

      console.log('Autosave completato');


    },
    toggleLeftPanelQuesito() {
      this.leftPanelVisibleQuesito = !this.leftPanelVisibleQuesito;
    },
    closeArticleModal() {
      this.showArticleModal = false;
      this.localElementId = null;
      this.modalArticleId = null;
      this.modalVersion = null;
      this.$emit('reset-element');
    },
    async replyToQuestionConfirmed() {
      this.isChangingState = true;

      const payload = {
        id: this.question.id,
        text: this.editableQuestion,
        answer: this.answerText,
        topics: this.topicsSnapshot.map(t => t.id),
        articles: this.articlesSnapshot.map(a => a.id),
        state_id: 'EVASO',
      };

      console.log('Payload:', payload);

      try {
        const response = await this.$root.$api.put(`/questions/${this.question.id}`, payload);

        // Aggiorna dati locali
        this.question.state_id = response.data.question.state_id;
        this.question.updated_at = response.data.question.updated_at;

        // Disabilita pulsanti
        this.isAnswered = true;

        // Mostra toast di successo
        //this.showSuccessToast("Operazione completata", "Quesito evaso correttamente");

        this.showSuccessModal = true;

      } catch (err) {
        console.error(err);
        this.showErrorToast("Errore", "Impossibile evadere il quesito");
      } finally {
        this.isChangingState = false;
      }
    },
    replyToQuestion() {
      const questionPayload = {
        id: this.question.id,
        text: this.editableQuestion,
        answer: this.answerText,
        topics: this.topicsSnapshot.map(t => ({ id: t.id, title: t.title })),
        articles: this.articlesSnapshot.map(a => ({
          id: a.id,
          title: a.versions?.[a.versions.length - 1]?.title || a.title
        })),
        expert_id: this.question.expert_id,
        state_id: 'INVIATO'
      };

      console.log('Question payload:', questionPayload);
    },
    getInitials(user) {
      if (!user || !user.name) return '';
      return `${user.name[0] || ''}${user.surname[0] || ''}`.toUpperCase();
    },
    formatDate(date) {
      const d = new Date(date);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
    },
    toggleComment(comment) {
      comment.expanded = !comment.expanded;
    },
    needsReadMore(comment) {
      return comment.text.length > 250;
    },

    openModalArticle() {
      const articleId = this.$route.params.article;
      const version = this.$route.params.version || null;

      this.modalArticleId = articleId;
      this.modalVersion = version;
      this.modalCounter++;
      //this.showArticleModal = true;

      this.$nextTick(() => {
        this.showArticleModal = true;
      });
    },
    async reloadQuestion() {
      this.isLoading = true;
      try {
        this.availableArticles = this.extractAllArticlesFromTree();
        await this.loadElements();
        await this.loadTopics();
        await this.loadQuestion();
        /**this.$nextTick(() => {
          this.availableArticles = this.extractAllArticlesFromTree();
          console.log("reloadQuestion 2");
        });*/
        console.log("reloadQuestion");
      } finally {
        this.isLoading = false;
      }
    },
    getArticleDisplayTitle(article) {
      if (!article) return '—';
      let title = '';

      // se ha versions
      if (article.versions && article.versions.length > 0) {
        title = article.versions[article.versions.length - 1].title || '';
      } else {
        // articolo semplice
        title = article.title || '';
      }

      return this.formatArticleTitle(title);
    },
    extractAllArticlesFromTree() {
      const all = this.$root.contents?.articles || {};

      return Object.values(all)
        .filter(a => !a.deleted_at && a.versions && a.versions.length > 0)
        .map(a => ({
          id: a.id,
          versions: a.versions
        }));
    },
    async loadTopics() {
      try {
        const [topicsResponse] = await Promise.all([
          this.$root.$api.get('/questions/topics'),
        ]);
        this.availableTopics = topicsResponse.data;
      } catch (error) {
        this.showErrorToast('Errore', 'Impossibile caricare argomenti o legislazioni');
      }
    },

    getTopicName(id) {
      const topic = this.availableTopics.find(t => t.id === id);
      return topic ? topic.title : '';
    },
    toggleLeftPanel() {
      this.leftPanelVisible = !this.leftPanelVisible;
    },
    formatArticleTitle(title) {
      if (!title) return '—';

      const match = title.match(/^Art\.\s*\d+/);
      if (match) return match[0];

      if (/^Articolo\s+\d+/i.test(title)) return title;

      return title;
    },
    removeArticle(index) {
      const removed = this.articlesSnapshot[index].id;
      this.articlesSnapshot.splice(index, 1);
      this.selectedArticlesIds = this.selectedArticlesIds.filter(id => id !== removed);
      this.question.articles = [...this.selectedArticlesIds];
    },
    removeTopic(index) {
      const removed = this.topicsSnapshot[index].id;
      this.topicsSnapshot.splice(index, 1);
      this.selectedTopicsIds = this.selectedTopicsIds.filter(id => id !== removed);
      this.question.topics = [...this.selectedTopicsIds];
    },
    formatState(state) {
      const map = {
        INVIATO: { label: "RICEVUTO", color: "yellow" },
        ASSEGNATO: { label: "ASSEGNATO", color: "blue" },
        IN_LAVORAZIONE: { label: "IN LAVORAZIONE", color: "purple" },
        RISPOSTA_IN_BOZZA: { label: "BOZZA", color: "gray" },
        EVASO: { label: "EVASO", color: "green" },
      };
      return map[state] || { label: state, color: "gray" };
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
    },
    async loadQuestion() {
      const response = await this.$root.$api.get(`/questions/${this.elementId}`);
      const q = response.data;

      const articlesArray = this.$root.contents?.articles || [];

      q.articles = (q.articles || []).map(a => {
        const content = articlesArray.find(c => c.id === a.id);

        if (!content || !content.versions?.length) {
          return { ...a, title: null };
        }

        const lastVersion = content.versions.reduce(
          (prev, curr) => (curr.id > prev.id ? curr : prev),
          content.versions[0]
        );

        return { ...a, title: lastVersion.title };
      });

      this.selectedTopicsIds = q.topics?.map(t => t.id) || [];
      this.selectedArticlesIds = q.articles?.map(a => a.id) || [];

      this.question = q;

      //this.question.expert_id = q.expert?.id ?? null;

      this.topicsSnapshot = q.topics?.map(t => ({ ...t })) || [];
      this.articlesSnapshot = q.articles?.map(a => ({ ...a })) || [];

      this.revisionText = this.question.text || '';

      this.$nextTick(() => {
        this.answerText = q.answer || '';
      });

      this.comments = (q.comments || [])
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .map(c => ({
          ...c,
          expanded: false
        }));
    },

    async loadElements() {
    }

  },
  async mounted() {
    const params = this.$root.params || {};
    const filters = params.filters || null;
    await this.reloadQuestion();

    if (filters) {
      this.applyFilterFromParams(filters);
    }

    this.autosaveInterval = setInterval(() => {
      this.saveDraft();
    }, 1500000);

    window.addEventListener('beforeunload', this.autosaveBeforeUnload);
  },

  beforeUnmount() {
    clearInterval(this.autosaveInterval);
    window.removeEventListener('beforeunload', this.autosaveBeforeUnload);
  }
};
</script>

<style scoped>
.question-detail-expert {
  height: 88vh !important;
  display: flex;
  gap: 1rem;
  background-color: rgba(255, 255, 255, 0.7);
  padding: 1rem;
  border-radius: 8px;
}

.left-panel {
  max-width: 25%;
  overflow-y: auto;
}

.right-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(180deg,
      #fff1f7 0%,
      #fff6fa 100%);
}

.question-body {
  flex: 1 1 0;
  display: flex;
  gap: 1rem;
  min-height: 0;
}

.question-body>div {
  min-width: 0 !important;
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  /* bianco per contrasto con rosa */
  border-radius: 8px;
  padding: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #ddd;
  min-height: 0;
}

.quesito-panel,
.quesito-panel>.no-grow,
.right-panel {
  min-width: 0 !important;
}

.question-body>div .fw-bold {
  margin-bottom: 0.5rem;
  font-size: 1rem;
  color: #333;
}

.question-body>div .ql-editor {
  min-height: 150px;
  /* altezza minima editor */
  background-color: rgba(255, 255, 255, 0.7);
  /* sfondo editor */
  border-radius: 4px;
  padding: 0.5rem;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.loading-text {
  margin-top: 1rem;
  font-weight: bold;
}

.status-label {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 1.1rem;
  white-space: nowrap;
  color: white;
}

.status-box {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  color: white;
  font-weight: 600;
}

.status-yellow {
  background: #ffc107;
  color: #fff;
}

.status-green {
  background-color: green;
  color: white;
}

.status-blue {
  background: #0d6efd;
  color: #fff;
}

.status-purple {
  background: #6f42c1;
  color: #fff;
}

.status-gray {
  background: #6c757d;
  color: #fff;
}

.small-rect-btn {
  height: 1rem;
  width: 1.5rem;
  padding: 0;
  font-size: 0.8rem;
  border-radius: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.tag-row {
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  flex-wrap: nowrap;
}

.tag-box {
  /**background-color: #e9ecef;*/
  background-color: grey;
  border: 1px solid #d3d3d3;
  padding: 0px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  white-space: nowrap;
  color: white;
}

.remove-btn {
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  padding: 0;
  line-height: 1;
  font-size: 0.9rem;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.remove-btn:hover {
  background-color: #b02a37;
}

.ql-editor {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important
}

.toggle-pill {
  position: relative;
  width: 220px;
  height: 50px;
  user-select: none;
}

.toggle-pill input.toggle-switch {
  display: none;
  /* nascondiamo checkbox reale */
}

.toggle-pill .toggle-label {
  display: block;
  width: 22rem;
  height: 2rem;
  background-color: #e9ecef;
  border-radius: 50px;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  border: 2px solid #0d6efd;
}

.toggle-pill .toggle-option {
  position: absolute;
  width: 50%;
  height: 100%;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  color: white;
  /* testo non selezionato nero */
  transition: color 0.3s;
  pointer-events: none;
  /* clicca solo sul label */
}

.toggle-pill .toggle-left {
  left: 0;
}

.toggle-pill .toggle-right {
  right: 0;
}

.toggle-pill .toggle-label::before {
  content: '';
  position: absolute;
  width: 50%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: #0d6efd;
  /* colore opzione attiva */
  border-radius: 50px;
  transition: left 0.3s;
}

.toggle-pill input.toggle-switch:checked+.toggle-label::before {
  left: 50%;
}

.toggle-pill input.toggle-switch:checked+.toggle-label .toggle-left {
  color: black;
  /* opzione selezionata */
}

.toggle-pill input.toggle-switch:not(:checked)+.toggle-label .toggle-right {
  color: black;
  /* opzione selezionata */
}

.toggle-3d {
  position: relative;
  width: 240px;
  height: 31px;
  perspective: 500px;
}

.toggle-3d input.toggle-switch {
  display: none;
}

.toggle-3d .toggle-label {
  display: block;
  width: 22rem;
  height: 2rem;
  background-color: #e9ecef;
  border-radius: 50px;
  padding: 5px;
  /* spazio interno per effetto “toggle flottante” */
  box-shadow: inset 0 4px 6px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.2);
  position: relative;
  cursor: pointer;
  overflow: hidden;
  border: 2px solid #0A2342;
}

.toggle-3d .toggle-option {
  position: absolute;
  width: 50%;
  height: 100%;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  color: white;
  transition: color 0.3s;
  pointer-events: none;
}

.toggle-3d .toggle-left {
  left: 0;
}

.toggle-3d .toggle-right {
  right: 0;
}

.toggle-3d .toggle-label::before {
  content: '';
  position: absolute;
  width: calc(50% - 10px);
  /* più piccolo del 50% per lasciare spazio bordo */
  height: calc(100% - 10px);
  top: 5px;
  left: 5px;
  background-color: #0A2342;
  border-radius: 50px;
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.2);
  transition: left 0.3s;
}

.toggle-3d input.toggle-switch:checked+.toggle-label::before {
  left: calc(50% + 5px);
}

.toggle-3d input.toggle-switch:checked+.toggle-label .toggle-left {
  color: black;
}

.toggle-3d input.toggle-switch:not(:checked)+.toggle-label .toggle-right {
  color: black;
}

.editor-container {
  min-height: 150px;
  width: 100%;
}

.compact-view section.container {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
}

.compact-view .row {
  margin: 0 !important;
}

.compact-view .col-12 {
  padding: 0.25rem !important;
}

.compact-view .m-2 {
  margin: 0.35rem !important;
}

.compact-view .p-3 {
  padding: 0.75rem !important;
}

.articles-left * {
  margin-bottom: 0.35rem !important;
}

.articles-left .m-2 {
  margin: 0.5rem 0 !important;
}

.articles-left .p-3 {
  padding: 0.5rem !important;
}

.scaled-articles {
  transform: scale(0.75);
  transform-origin: top left;
  width: 133%;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.left-toggle {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  background-color: #f8f9fa;
  border-radius: 4px;
  margin-right: 4px;
  user-select: none;
}

.argomenti-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 100%;
}

.tag-row-wrapper {
  display: flex;
  gap: 6px;
  margin-bottom: 4px;
}

.tag-row {
  display: flex;
  align-items: center;
  background: #f1f1f1;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

.tag-box {
  margin-right: 4px;
}

.comments-view {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.comment-postit {
  background-color: #fff9c4;
  /* giallo post-it */
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  max-width: 100%;
}

.comment-header .avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #fbc02d;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.comment-text.collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  display: box;
  box-orient: vertical;
  line-clamp: 5;
  overflow: hidden;
}

.comment-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: red;
  color: white;
  font-size: 0.8rem;
  margin-left: 4px;
}

.tiptap-editor {
  height: 100%;
}

.editor-wrapper {
  flex: 1 1 0;
  min-height: 0;
}

.editor-content {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;

  flex-grow: 1;
  min-height: 150px;
  padding: 8px;
  border: 1px solid #eee;
  border-radius: 4px;
  background: #fff !important;
  outline: none !important;
  box-shadow: none !important;
}

.original-box {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #fff;
  flex: 1;
}

.quesito-panel,
.risposta-panel,
.tiptap-wrapper .ProseMirror {
  white-space: pre-wrap !important;
  /* va a capo sui ritorni a capo */
  word-break: break-word !important;
  /* forza a capo parole lunghe */
  overflow-wrap: break-word !important;
  /* compatibilità browser */
  min-width: 0 !important;
  /* permette al flex di restringersi */
}

.no-grow {
  min-width: 0 !important;
  max-width: 100% !important;
}

.tiptap,
.tiptap * {
  overflow-wrap: break-word !important;
  word-break: break-word !important;
  white-space: pre-wrap !important;
}

.tiptap-wrapper {
  min-width: 0 !important;
  max-width: 100% !important;
}

.comment-yellow {
  background-color: #fff3b0;
  /* giallo chiaro come post-it */
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  position: relative;
}

.comment-yellow .thread-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.comment-yellow .avatar {
  width: 32px;
  height: 32px;
  background-color: #6c757d;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 0.5rem;
  font-size: 0.9rem;
}

.comment-yellow .user-name {
  font-weight: 600;
}

.comment-yellow .comment-date {
  font-size: 0.75rem;
  color: #6c757d;
}

.comment-yellow .thread-content {
  margin-bottom: 0.5rem;
  display: inline !important;
}

.comment-yellow .thread-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.comment-date {
  font-size: 0.75rem;
  color: #6c757d;
}

.thread-header-common {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.thread-header-common .avatar {
  width: 32px;
  height: 32px;
  background-color: #6c757d;
  /* Colore avatar */
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 0.5rem;
  font-size: 0.9rem;
}

.thread-header-common .user-name {
  font-weight: 600;
}

.thread-header-common .comment-date {
  font-size: 0.75rem;
  color: #6c757d;
}

/* Background e bordo colorato per tipo */
.thread-item-original {
  background-color: #e0f7fa;
  border-left: 4px solid #00acc1;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.thread-item-revision {
  background-color: #e0f7fa;
  border-left: 4px solid #00acc1;
  /**background-color: #f3e5f5;
  border-left: 4px solid #8e24aa;*/
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.thread-item-answer {
  background-color: #e0f7fa;
  border-left: 4px solid #00acc1;
  /**background-color: #fff9c4;
  border-left: 4px solid #fbc02d;*/
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.thread-label {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: 0.25rem;
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  color: white;
}

/** .label-original {
  background-color: #00acc1;
}

.label-revision {
  background-color: #8e24aa;
}

.label-answer {
  background-color: #fbc02d;
  color: #333;
}*/

.label-original {
  background-color: black;
  color: white;
}

.label-revision {
  background-color: black;
  color: white;
}

.label-answer {
  background-color: black;
  color: white;
}

.thread-item {
  position: relative;
}

.thread-content ::v-deep(mark) {
  display: inline !important;
  margin: 0;
  padding: 0;
  line-height: inherit;
}

.mark,
mark {
  display: inline !important;
  padding: .1875em;
  color: var(--bs-highlight-color);
  background-color: var(--bs-highlight-bg);
}
</style>
